import { computed, watch } from 'vue';
import { SPECIES_AND_BREEDS_GENERATED } from '@AnimalModule';
import { type AllSpeciesWithBreeds } from '@AnimalModule';
import { type FiltersProps, type OptionArray, SYSTEM_FILTERS } from '../types';
import useActiveFilters from './useActiveFilters';
import useFilters from './useFilters';

const { _activeFilters } = useActiveFilters();
const { systemFilters } = useFilters();

const speciesWithBreeds = computed((): AllSpeciesWithBreeds => SPECIES_AND_BREEDS_GENERATED);
const DEFAULT_SPECIES = ['Dog', 'Cat'];

export default function () {
  let initialBreedsMessage: string | undefined = '';

  const speciesFilterModel = computed(() => {
    const speciesFilter = _activeFilters.value.filter(
      ({ name }) => name === systemFilters()[SYSTEM_FILTERS.species].name
    );

    return speciesFilter.length ? speciesFilter[0].model : undefined;
  });

  watch(speciesFilterModel, (model) => onSpeciesFilterUpdate(model as OptionArray[]));

  function setSpecies(filterSpecies: string[], speciesFilter: FiltersProps) {
    const species = _setDefaultSpecies([...new Set(filterSpecies)]);

    const speciesOptions = species.map((specie) => {
      const match = speciesWithBreeds.value[specie];
      let result = { name: specie, value: specie };

      if (match) result = { name: match.name, value: match.value };

      return result;
    });

    speciesFilter.settings.options = speciesOptions;
  }

  function _setDefaultSpecies(species: string[]) {
    const orderedDefault = species
      .filter((specie) => DEFAULT_SPECIES.includes(specie))
      .sort((a, b) => DEFAULT_SPECIES.indexOf(a) - DEFAULT_SPECIES.indexOf(b));

    const otherSpecies = species.filter((item) => !DEFAULT_SPECIES.includes(item)).sort();

    return [...orderedDefault, ...otherSpecies];
  }

  function orderSpeciesAnBreed(filters: FiltersProps[]) {
    const speciesIndex = filters.findIndex((obj) => obj.name === SYSTEM_FILTERS.species);
    const breedsIndex = filters.findIndex((obj) => obj.name === SYSTEM_FILTERS.breed);

    if (speciesIndex !== -1 && breedsIndex !== -1) {
      const breedsFilter = filters.splice(breedsIndex, 1)[0];
      filters.splice(speciesIndex + 1, 0, breedsFilter);
    }

    return filters;
  }

  // NOTE :: A shelter whose templates give only one species (e.g. only Dog) should not have to pick it before
  // filtering by breed. This returns that species' breeds, or [] when there are 2+ species or the one species has
  // no breed list (Barn, Reptile and other subspecies have none). It runs from the species watcher, not from
  // getDynamicFilters: the filters dialog deep-copies the filters on every open, Clear, and saved report, which
  // re-fires the watcher and would lock Breed again.
  function getSingleSpeciesBreeds() {
    const speciesFilter = _activeFilters.value.find(
      ({ name }) => name === systemFilters()[SYSTEM_FILTERS.species].name
    );

    const speciesOptions = speciesFilter?.settings.options ?? [];
    if (speciesOptions.length !== 1) return [];

    return speciesWithBreeds.value[speciesOptions[0].value]?.breeds ?? [];
  }

  function onSpeciesFilterUpdate(model: OptionArray[]) {
    const noSpeciesSelected = !model || !model.length;

    const breedsFilter = _activeFilters.value.filter(
      ({ name }) => name === systemFilters()[SYSTEM_FILTERS.breed].name
    )[0];

    if (!breedsFilter) return;

    handleBreedsHint(breedsFilter, model.length);

    const singleSpeciesBreeds = noSpeciesSelected ? getSingleSpeciesBreeds() : [];
    const shouldUnlockSingleSpeciesBreeds = noSpeciesSelected && !!singleSpeciesBreeds.length;
    if (shouldUnlockSingleSpeciesBreeds) {
      // NOTE :: Runs after handleBreedsHint, so it can drop the "Select species first" hint it just set.
      // The breed model is kept, so a breed the user already picked stays picked.
      if (breedsFilter.state) breedsFilter.state.disabled = false;

      if (breedsFilter.text) breedsFilter.text.hint = '';

      breedsFilter.settings.options = singleSpeciesBreeds;
      return;
    }

    if (noSpeciesSelected) {
      clearBreeds();
      return;
    }

    const multipleSpeciesSelect = model.length > 1;
    if (multipleSpeciesSelect) {
      clearBreeds();
      return;
    }

    const breedMatch = speciesWithBreeds.value[model[0].value];
    const breeds = breedMatch ? breedMatch.breeds : [];

    const selectedSpecieHasNoBreeds = !breeds.length;
    if (selectedSpecieHasNoBreeds) return;

    // Populate breeds
    if (breedsFilter.state) breedsFilter.state.disabled = false;
    breedsFilter.settings.options = breeds;

    function clearBreeds() {
      if (breedsFilter.state) breedsFilter.state.disabled = true;
      breedsFilter.model = [];
      breedsFilter.settings.options = [];
    }

    function handleBreedsHint(breedsFilter: FiltersProps, selectedSpecies: number) {
      if (!initialBreedsMessage && breedsFilter.text) {
        initialBreedsMessage = breedsFilter.text.hint;
      }

      if (breedsFilter.text) {
        switch (selectedSpecies) {
          case 0:
            breedsFilter.text.hint = initialBreedsMessage;
            break;
          case 1:
            breedsFilter.text.hint = '';
            break;
          default:
            breedsFilter.text.hint = 'filters.hint.onlyOneSpecie';
        }
      }
    }
  }

  return {
    setSpecies,
    orderSpeciesAnBreed,
  };
}
